import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildCalendarDays,
  buildEventDateMap,
  formatDateKey,
  formatEventKind,
  formatTime,
  parseDate,
  parseEventNotes,
  startOfMonth,
} from '@car-pool/shared/calendar';
import { apiFetch } from '../config/api';
import SchoolLayout from '../components/SchoolLayout';
import CalendarCsvImportModal from '../components/CalendarCsvImportModal';
import {
  formatCalendarEventTiming,
  getCalendarEventDisplayTime,
} from '../lib/calendarEvents';

const EVENT_TYPE_OPTIONS = [
  { value: 'CUSTOM', label: 'School Event' },
  { value: 'EARLY_RELEASE', label: 'Early Release' },
  { value: 'LATE_START', label: 'Late Start' },
  { value: 'NO_SCHOOL', label: 'No School' },
];

const DISMISSAL_TIME_OPTIONS = [
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
];

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function formatSelectedDate(dateKey) {
  const date = parseDate(dateKey);
  if (!date) return 'Schedule';
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', weekday: 'long' }).replace(',', ' —');
}

function normalizeDateKeyInput(value) {
  const parsed = parseDate(value);
  if (!parsed) return '';
  return formatDateKey(parsed);
}

function getWeekdayCode(dateKey) {
  const date = parseDate(dateKey);
  return date ? WEEKDAY_CODES[date.getDay()] : '';
}

function getGradeLabel(grade, labelMap = {}) {
  if (grade === null || grade === undefined || grade === '' || grade === 'ALL') return 'All Grades';

  const value = Number(grade);
  if (Number.isNaN(value)) return String(grade);
  if (labelMap[value]) return labelMap[value];
  if (value === 0) return 'Kindergarten';
  if (value === -1) return 'Pre-K';
  if (value === -2) return 'Preschool';
  if (value === -3) return 'Pre-Preschool';

  const remainderTen = value % 10;
  const remainderHundred = value % 100;
  let suffix = 'th';

  if (remainderTen === 1 && remainderHundred !== 11) suffix = 'st';
  if (remainderTen === 2 && remainderHundred !== 12) suffix = 'nd';
  if (remainderTen === 3 && remainderHundred !== 13) suffix = 'rd';

  return `${value}${suffix} Grade`;
}

function isNoDismissalDefault(row) {
  return row?.is_no_dismissal === true
    || row?.is_no_dismissal === 1
    || String(row?.is_no_dismissal) === '1';
}

function MonthCalendar({ monthDate, selectedDate, eventDateMap, onSelect, onMonthChange, compact = false }) {
  const days = useMemo(() => buildCalendarDays(monthDate), [monthDate]);

  return (
    <div className={`sca-month ${compact ? 'compact' : ''}`}>
      <div className="sca-month-head">
        <button type="button" onClick={() => onMonthChange(-1)} aria-label="Previous month">‹</button>
        <div>{monthDate.toLocaleDateString([], { month: 'long', year: 'numeric' })}</div>
        <button type="button" onClick={() => onMonthChange(1)} aria-label="Next month">›</button>
      </div>

      <div className="sca-weekdays">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day) => <span key={`${compact ? 'c' : 'm'}-${day}`}>{day}</span>)}
      </div>

      <div className="sca-days">
        {days.map((day) => {
          const dotCount = Math.min(eventDateMap.get(day.key)?.length || 0, 3);
          return (
            <button
              key={`${compact ? 'compact' : 'main'}-${day.key}`}
              type="button"
              className={`sca-day ${day.inMonth ? '' : 'muted'} ${selectedDate === day.key ? 'selected' : ''} ${dotCount > 0 ? 'has-event' : ''}`}
              onClick={() => onSelect(day.key)}
            >
              <span className="sca-day-label">{day.day}</span>
              <span className="sca-day-dots" aria-hidden="true">
                {Array.from({ length: dotCount }, (_, index) => (
                  <span key={`${day.key}-dot-${index}`} className="sca-day-dot"></span>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarPage({ token, navigate, schoolId, user, bare = false }) {
  const todayKey = formatDateKey(new Date());
  const [events, setEvents] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [school, setSchool] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [selectedMonth, setSelectedMonth] = useState(startOfMonth(new Date()));
  const [form, setForm] = useState({
    title: '',
    kind: 'CUSTOM',
    dismissalTime: '15:00',
    notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [csvOpen, setCsvOpen] = useState(false);

  useEffect(() => {
    setSelectedDate(todayKey);
    setSelectedMonth(startOfMonth(new Date()));
  }, [schoolId, todayKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [eventsRes, gradesRes, schoolRes] = await Promise.all([
        apiFetch(`/dismissal-events?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/schools/${schoolId}`, {}, token).catch(() => ({ data: null })),
      ]);

      setEvents(eventsRes.data || []);
      setGradeNames((gradesRes.data || []).filter((grade) => grade.is_active));
      setSchool(schoolRes.data || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    const weekday = getWeekdayCode(selectedDate);
    const weekdayParam = weekday ? `&weekday=${weekday}` : '';

    apiFetch(`/dismissal-time-defaults/active?school_id=${schoolId}&date=${selectedDate}${weekdayParam}`, {}, token)
      .then((res) => {
        if (!cancelled) setDefaults(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setDefaults([]);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedDate, token]);

  const gradeLabelMap = useMemo(
    () => Object.fromEntries(gradeNames.map((grade) => [Number(grade.grade_code), grade.label])),
    [gradeNames]
  );

  const eventDateMap = useMemo(() => buildEventDateMap(events), [events]);

  useEffect(() => {
    const date = parseDate(selectedDate);
    if (date) {
      setSelectedMonth((current) => {
        if (current.getFullYear() === date.getFullYear() && current.getMonth() === date.getMonth()) {
          return current;
        }
        return startOfMonth(date);
      });
    }
  }, [selectedDate]);

  const selectedDayEvents = useMemo(() => {
    return [...(eventDateMap.get(selectedDate) || [])].sort((left, right) => String(left.start_time || '').localeCompare(String(right.start_time || '')));
  }, [eventDateMap, selectedDate]);

  const selectedDayItems = useMemo(() => {
    if (selectedDayEvents.length > 0) {
      return selectedDayEvents.map((event) => {
        const noteParts = parseEventNotes(event.notes);
        const timeLabel = formatCalendarEventTiming(event);

        return {
          id: `event-${event.id}`,
          title: noteParts.title,
          subtitle: timeLabel,
          detail: noteParts.detail || formatEventKind(event.kind),
        };
      });
    }

    const defaultItems = defaults
      .sort((left, right) => (
        Number(isNoDismissalDefault(right)) - Number(isNoDismissalDefault(left))
          || String(left.dismissal_time || '').localeCompare(String(right.dismissal_time || ''))
      ))
      .map((item) => ({
        id: `default-${item.id}`,
        title: isNoDismissalDefault(item) ? 'No Dismissal' : 'Regular Dismissal',
        subtitle: isNoDismissalDefault(item) ? 'No dismissal' : `Dismissal Time: ${formatTime(item.dismissal_time)}`,
        detail: getGradeLabel(item.grade, gradeLabelMap),
      }));

    if (defaultItems.length > 0) return defaultItems;

    return school?.default_dismissal_time
      ? [{
          id: 'school-default',
          title: 'Regular Dismissal',
          subtitle: `Dismissal Time: ${formatTime(school.default_dismissal_time)}`,
          detail: 'All Grades',
        }]
      : [];
  }, [defaults, gradeLabelMap, school, selectedDayEvents]);

  const scheduleChanges = useMemo(() => {
    const future = events.filter((event) => String(event.start_date) >= selectedDate);
    const source = future.length ? future : [...events].sort((left, right) => String(right.start_date).localeCompare(String(left.start_date)));

    return source
      .sort((left, right) => String(left.start_date).localeCompare(String(right.start_date)))
      .slice(0, 6);
  }, [events, selectedDate]);

  const setFormField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleMonthChange = (offset) => {
    setSelectedMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!form.title.trim()) {
        throw new Error('Event title is required');
      }

      const normalizedSelectedDate = normalizeDateKeyInput(selectedDate);
      if (!normalizedSelectedDate) {
        throw new Error('Selected date is invalid');
      }

      const normalizedTime = `${form.dismissalTime}:00`;
      const isEarlyRelease = form.kind === 'EARLY_RELEASE';
      const isLateStart = form.kind === 'LATE_START';
      const isTimedEvent = isEarlyRelease || isLateStart;
      const noteBody = [form.title.trim(), form.notes.trim()].filter(Boolean).join('\n');

      await apiFetch('/dismissal-events', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          start_date: normalizedSelectedDate,
          end_date: normalizedSelectedDate,
          grade: 'ALL',
          gender: null,
          kind: form.kind,
          notes: noteBody,
          start_time: isTimedEvent ? normalizedTime : null,
          end_time: isEarlyRelease ? normalizedTime : null,
        }),
      }, token);

      setSuccess('Schedule event created');
      setForm((current) => ({ ...current, title: '', notes: '' }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div className="sap-page">
        {error ? <div className="sa-error sap-inline-error">{error}</div> : null}
        {success ? <div className="sa-success sap-inline-error">{success}</div> : null}

        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : (
          <div className="sca-shell">
            <section className="sap-parents-panel sca-month-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>School Calendar</h2>
                  <p>View upcoming dismissal changes and school events</p>
                </div>
              </div>

              <MonthCalendar
                monthDate={selectedMonth}
                selectedDate={selectedDate}
                eventDateMap={eventDateMap}
                onSelect={setSelectedDate}
                onMonthChange={handleMonthChange}
              />
            </section>

            <section className="sap-parents-panel sca-selected-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>{formatSelectedDate(selectedDate)}</h2>
                  <p>Selected dismissal schedule</p>
                </div>
              </div>

              <div className="sca-day-detail">
                {selectedDayItems.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">No schedule changes are recorded for this date.</div>
                ) : (
                  selectedDayItems.map((item) => (
                    <div key={item.id} className="sca-day-card">
                      <div className="sca-day-card-title">{item.title}</div>
                      <div className="sca-day-card-subtitle">{item.subtitle}</div>
                      <div className="sca-day-card-detail">{item.detail}</div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="sap-parents-panel sca-upcoming-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Upcoming Schedule Changes</h2>
                  <p>Recent and upcoming dismissal adjustments</p>
                </div>
              </div>

              <div className="sca-upcoming-list">
                {scheduleChanges.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">No dismissal events have been created for this school yet.</div>
                ) : (
                  scheduleChanges.map((eventItem) => {
                    const noteParts = parseEventNotes(eventItem.notes);
                    return (
                      <div key={eventItem.id} className="sca-upcoming-item">
                        <div className="sca-upcoming-date">
                          <span>{parseDate(eventItem.start_date)?.toLocaleDateString([], { month: 'short' }) || '—'}</span>
                          <strong>{parseDate(eventItem.start_date)?.getDate() || '—'}</strong>
                        </div>
                        <div className="sca-upcoming-copy">
                          <div className="sca-upcoming-title">{noteParts.title}</div>
                          <div className="sca-upcoming-meta">
                            {formatEventKind(eventItem.kind)}
                            {getCalendarEventDisplayTime(eventItem) ? ` · ${formatTime(getCalendarEventDisplayTime(eventItem))}` : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="sap-parents-panel sca-form-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Add Event</h2>
                  <p>Create a schedule change or school event</p>
                </div>
              </div>

              <form className="sca-form" onSubmit={handleSubmit}>
                <input
                  className="sca-input"
                  value={form.title}
                  onChange={setFormField('title')}
                  placeholder="Enter event title"
                />

                <select className="sca-input" value={form.kind} onChange={setFormField('kind')}>
                  {EVENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                <MonthCalendar
                  monthDate={selectedMonth}
                  selectedDate={selectedDate}
                  eventDateMap={eventDateMap}
                  onSelect={setSelectedDate}
                  onMonthChange={handleMonthChange}
                  compact
                />

                {['EARLY_RELEASE', 'LATE_START'].includes(form.kind) ? (
                  <select
                    className="sca-input"
                    value={form.dismissalTime}
                    onChange={setFormField('dismissalTime')}
                    aria-label={form.kind === 'LATE_START' ? 'Late start time' : 'Dismissal time'}
                  >
                    {DISMISSAL_TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>{formatTime(`${time}:00`)}</option>
                    ))}
                  </select>
                ) : null}

                <textarea
                  className="sca-notes"
                  value={form.notes}
                  onChange={setFormField('notes')}
                  placeholder="Add notes for staff and dismissal planning"
                />

                <button type="submit" className="sa-btn sa-btn-primary sca-submit" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Event'}
                </button>
                <button
                  type="button"
                  className="sa-btn sa-btn-secondary sca-submit sca-import-btn"
                  onClick={() => setCsvOpen(true)}
                >
                  Import CSV
                </button>
              </form>
            </section>
          </div>
        )}

        {csvOpen ? (
          <CalendarCsvImportModal
            token={token}
            schoolId={schoolId}
            onClose={() => setCsvOpen(false)}
            onImported={() => {
              setSuccess('CSV import complete');
              load();
            }}
          />
        ) : null}
      </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="calendar"
      breadcrumb="Calendar"
    >
      {content}
    </SchoolLayout>
  );
}
